import { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import lazyPage from './utils/lazyPage';
import PageLoading from './components/PageLoading';

// Auth
import ProtectedRoute      from './auth/ProtectedRoute';
import TribeProtectedRoute from './pages/Tribe/TribeProtectedRoute';

// Layout
import MainLayout from './layouts/MainLayout';

// Pages below HomePage load on demand (utils/lazyPage), each as its own file.

// Public Pages
import HomePage       from './pages/Home/HomePage';
import RoadReadyRedirect from './components/RoadReadyRedirect';

const ApplyPage      = lazyPage(() => import('./pages/Apply/ApplyPage'));
const AboutPage      = lazyPage(() => import('./pages/About/AboutPage'));
const ProgramsPage   = lazyPage(() => import('./pages/Programs/ProgramsPage'));
const LocationsPage  = lazyPage(() => import('./pages/Locations/LocationsPage'));
const FinancingPage  = lazyPage(() => import('./pages/financing/FinancingPage'));
const VeteransPage   = lazyPage(() => import('./pages/Veterans/VeteransPage'));
const FAQPage        = lazyPage(() => import('./pages/FAQ/FAQPage'));
const ContactPage    = lazyPage(() => import('./pages/Contact/ContactPage'));
const CalculatorPage = lazyPage(() => import('./pages/Calculator/CalculatorPage'));
const BoxTruckPage   = lazyPage(() => import('./pages/BoxTruck/BoxTruckPage'));
const AdvertiserPage = lazyPage(() => import('./pages/Advertiser/AdvertiserPage'));
const VideoGalleryPage = lazyPage(() => import('./pages/Commercials/VideoGalleryPage'));
const DriverProtectionPage = lazyPage(() => import('./pages/DriverProtection/DriverProtectionPage'));
const GiftCertificatePage = lazyPage(() => import('./pages/GiftCertificate/GiftCertificatePage'));
const TrailerTypesPage = lazyPage(() => import('./pages/TrailerTypes/TrailerTypesPage'));
const EldtStudyGuidePage = lazyPage(() => import('./pages/Programs/EldtStudyGuidePage'));
const BlogPage           = lazyPage(() => import('./pages/Blog/BlogPage'));
const CareerServicesPage = lazyPage(() => import('./pages/CareerServices/CareerServicesPage'));
const RecruitingPage     = lazyPage(() => import('./pages/Recruiting/RecruitingPage'));
const RoadReadyPage       = lazyPage(() => import('./pages/RoadReady/RoadReadyPage'));
const DispatcherCoursePage = lazyPage(() => import('./pages/Dispatcher/DispatcherCoursePage'));
const RealityShowPage      = lazyPage(() => import('./pages/RealityShow/RealityShowPage'));
const PodcastPage          = lazyPage(() => import('./pages/Podcast/PodcastPage'));
const RetailPage           = lazyPage(() => import('./pages/Retail/RetailPage'));
const BookletsPage         = lazyPage(() => import('./pages/Booklets/BookletsPage'));
const DonateEquipmentPage      = lazyPage(() => import('./pages/DonateEquipment/DonateEquipmentPage'));
const OwnerOperatorPage        = lazyPage(() => import('./pages/OwnerOperator/OwnerOperatorPage'));
const AdminCertificates    = lazyPage(() => import('./pages/Admin/certificates/AdminCertificates'));
const AdminAttendance      = lazyPage(() => import('./pages/Admin/attendance/AdminAttendance'));
const AdminAuditLog        = lazyPage(() => import('./pages/Admin/auditlog/AdminAuditLog'));
const AdminEmailLog        = lazyPage(() => import('./pages/Admin/emaillog/AdminEmailLog'));
const AdminEldtProducts      = lazyPage(() => import('./pages/Admin/eldt/AdminEldtProducts'));
const AdminEldtOrders        = lazyPage(() => import('./pages/Admin/eldt/AdminEldtOrders'));
const InfluencerApplyPage    = lazyPage(() => import('./pages/Influencer/InfluencerApplyPage'));
const StudentAttendancePage    = lazyPage(() => import('./pages/StudentPortal/attendance/StudentAttendancePage'));
const StudentCertificatesPage  = lazyPage(() => import('./pages/StudentPortal/certificates/StudentCertificatesPage'));

// Apply Flow
const TrackApplicationPage = lazyPage(() => import('./pages/Apply/TrackApplicationPage'));
const CreateAccountPage    = lazyPage(() => import('./pages/Apply/CreateAccountPage'));

// QR
const QRScanPage = lazyPage(() => import('./pages/QR/QRScanPage'));

// Role-specific Login Pages
const AdminLoginPage      = lazyPage(() => import('./pages/Admin/AdminLoginPage'));
const InstructorLoginPage = lazyPage(() => import('./pages/Instructor/InstructorLoginPage'));
const StudentLoginPage    = lazyPage(() => import('./pages/StudentPortal/StudentLoginPage'));

// Dashboards
const AdminDashboard      = lazyPage(() => import('./pages/Admin/Dashboard'));
const InstructorDashboard = lazyPage(() => import('./pages/Instructor/InstructorDashboard'));
const StudentDashboard    = lazyPage(() => import('./pages/StudentPortal/StudentDashboard'));
const TrackingPage        = lazyPage(() => import('./pages/TrackingPage'));
// Admin Support
const AdminLayout       = lazyPage(() => import('./pages/Admin/layout/AdminLayout'));
const ContactSupport    = lazyPage(() => import('./pages/Admin/support/ContactSupport'));
const AdminTestimonials = lazyPage(() => import('./pages/Admin/testimonials/AdminTestimonials'));

// ✅ FIXED: casing matches folder on disk → Admin (capital A), not admin
const AdminMapMonitor = lazyPage(() => import('./pages/Admin/AdminMapMonitor/AdminMapMonitor'));

// Student Support
const StudentLayout  = lazyPage(() => import('./pages/StudentPortal/layout/StudentLayout'));
const StudentSupport = lazyPage(() => import('./pages/StudentPortal/support/StudentSupport'));

// Other
const EmployerPortalPage = lazyPage(() => import('./pages/employer/EmployerPortalPage'));
const SponsorPage        = lazyPage(() => import('./pages/Sponsor/SponsorPage'));
const ResetPasswordPage  = lazyPage(() => import('./pages/StudentPortal/ResetPasswordPage'));
const StudentMaps        = lazyPage(() => import('./pages/StudentPortal/maps/StudentMaps'));

// ── Tribe ──────────────────────────────────────────────────────
const TribeLoginPage = lazyPage(() => import('./pages/Tribe/TribeLoginPage'));
const TribeDashboard = lazyPage(() => import('./pages/Tribe/TribeDashboard'));
const GalleryPage    = lazyPage(() => import('./pages/Gallery/GalleryPage'));


function App() {
  return (
    <Router>
      <Suspense fallback={<PageLoading fullScreen />}>
      <Routes>

        {/* ── Public Routes ── */}
        <Route path="/"                       element={<MainLayout><HomePage /></MainLayout>} />
        <Route path="/about"                  element={<MainLayout><Suspense fallback={<PageLoading />}><AboutPage /></Suspense></MainLayout>} />
        <Route path="/programs"               element={<MainLayout><Suspense fallback={<PageLoading />}><ProgramsPage /></Suspense></MainLayout>} />
        <Route path="/locations"              element={<MainLayout><Suspense fallback={<PageLoading />}><LocationsPage /></Suspense></MainLayout>} />
        <Route path="/financing"              element={<MainLayout><Suspense fallback={<PageLoading />}><FinancingPage /></Suspense></MainLayout>} />
        <Route path="/veterans"               element={<MainLayout><Suspense fallback={<PageLoading />}><VeteransPage /></Suspense></MainLayout>} />
        <Route path="/faq"                    element={<MainLayout><Suspense fallback={<PageLoading />}><FAQPage /></Suspense></MainLayout>} />
        <Route path="/contact"                element={<MainLayout><Suspense fallback={<PageLoading />}><ContactPage /></Suspense></MainLayout>} />
        <Route path="/calculator"             element={<MainLayout><Suspense fallback={<PageLoading />}><CalculatorPage /></Suspense></MainLayout>} />
        <Route path="/box-truck"              element={<MainLayout><Suspense fallback={<PageLoading />}><BoxTruckPage /></Suspense></MainLayout>} />
        <Route path="/apply"                  element={<ApplyPage />} />
        <Route path="/track-application"      element={<TrackApplicationPage />} />
        <Route path="/create-account"         element={<CreateAccountPage />} />
        <Route path="/attendance/scan/:token" element={<QRScanPage />} />
        <Route path="/employer-portal"        element={<MainLayout><Suspense fallback={<PageLoading />}><EmployerPortalPage /></Suspense></MainLayout>} />
        <Route path="/reset-password"         element={<ResetPasswordPage />} />
        <Route path="/sponsor"                element={<MainLayout><Suspense fallback={<PageLoading />}><SponsorPage /></Suspense></MainLayout>} />
        <Route path="/advertise"              element={<MainLayout><Suspense fallback={<PageLoading />}><AdvertiserPage /></Suspense></MainLayout>} />
        <Route path="/commercials"            element={<MainLayout><Suspense fallback={<PageLoading />}><VideoGalleryPage /></Suspense></MainLayout>} />
        <Route path="/gallery"                element={<MainLayout><Suspense fallback={<PageLoading />}><GalleryPage /></Suspense></MainLayout>} />
        <Route path="/driver-protection" element={<MainLayout><Suspense fallback={<PageLoading />}><DriverProtectionPage /></Suspense></MainLayout>} /> 
        <Route path="/gift-certificates" element={<MainLayout><Suspense fallback={<PageLoading />}><GiftCertificatePage /></Suspense></MainLayout>} />
        <Route path="/trailer-types" element={<MainLayout><Suspense fallback={<PageLoading />}><TrailerTypesPage /></Suspense></MainLayout>} />
        <Route path="/eldt-study-guide" element={<MainLayout><Suspense fallback={<PageLoading />}><EldtStudyGuidePage /></Suspense></MainLayout>} />
        <Route path="/blog"             element={<MainLayout><Suspense fallback={<PageLoading />}><BlogPage /></Suspense></MainLayout>} />
        <Route path="/career-services" element={<MainLayout><Suspense fallback={<PageLoading />}><CareerServicesPage /></Suspense></MainLayout>} />
        <Route path="/road-ready-certified" element={<MainLayout><Suspense fallback={<PageLoading />}><RoadReadyPage /></Suspense></MainLayout>} />
        <Route path="/dispatcher-course"    element={<MainLayout><Suspense fallback={<PageLoading />}><DispatcherCoursePage /></Suspense></MainLayout>} />
        <Route path="/reality-show"         element={<MainLayout><Suspense fallback={<PageLoading />}><RealityShowPage /></Suspense></MainLayout>} />
        <Route path="/podcast"              element={<MainLayout><Suspense fallback={<PageLoading />}><PodcastPage /></Suspense></MainLayout>} />
        <Route path="/retail"               element={<MainLayout><Suspense fallback={<PageLoading />}><RetailPage /></Suspense></MainLayout>} />
        <Route path="/booklets"             element={<MainLayout><Suspense fallback={<PageLoading />}><BookletsPage /></Suspense></MainLayout>} />
        <Route path="/founding-partner"       element={<RoadReadyRedirect to="/" />} />
        <Route path="/founding-partner/apply" element={<RoadReadyRedirect to="/apply" />} />
        <Route path="/founding-partner/pay"   element={<RoadReadyRedirect to="/pay" />} />
        <Route path="/founding-partner/claim/:token" element={<RoadReadyRedirect to="/claim/:token" />} />
        <Route path="/celebrity/login"     element={<RoadReadyRedirect to="/influencer/login" />} />
        <Route path="/celebrity/dashboard" element={<RoadReadyRedirect to="/influencer/dashboard" />} />
        <Route path="/influencer/apply"    element={<InfluencerApplyPage />} />
        <Route path="/road-ready-network"   element={<RoadReadyRedirect to="/directory" />} />
        <Route path="/donate-equipment"     element={<MainLayout><Suspense fallback={<PageLoading />}><DonateEquipmentPage /></Suspense></MainLayout>} />
        <Route path="/owner-operator-course" element={<MainLayout><Suspense fallback={<PageLoading />}><OwnerOperatorPage /></Suspense></MainLayout>} />
        <Route path="/admin/certificates"   element={<ProtectedRoute roles={['admin','superadmin']} loginPath="/admin/login"><AdminCertificates /></ProtectedRoute>} />
        <Route path="/admin/attendance"     element={<ProtectedRoute roles={['admin','superadmin']} loginPath="/admin/login"><AdminAttendance /></ProtectedRoute>} />
        <Route path="/admin/audit-log"      element={<ProtectedRoute roles={['admin','superadmin']} loginPath="/admin/login"><AdminAuditLog /></ProtectedRoute>} />
        <Route path="/admin/email-log"      element={<ProtectedRoute roles={['admin','superadmin']} loginPath="/admin/login"><AdminEmailLog /></ProtectedRoute>} />
        <Route path="/admin/eldt-products"  element={<ProtectedRoute roles={['admin','superadmin']} loginPath="/admin/login"><AdminEldtProducts /></ProtectedRoute>} />
        <Route path="/admin/eldt-orders"       element={<ProtectedRoute roles={['admin','superadmin']} loginPath="/admin/login"><AdminEldtOrders /></ProtectedRoute>} />
        <Route path="/recruiting"           element={<MainLayout><Suspense fallback={<PageLoading />}><RecruitingPage /></Suspense></MainLayout>} />
        <Route path="/student/maps"           element={<StudentLayout><StudentMaps /></StudentLayout>} />
        <Route path="/student/attendance"     element={<ProtectedRoute roles={['student']} loginPath="/student/login"><StudentLayout><StudentAttendancePage /></StudentLayout></ProtectedRoute>} />
        <Route path="/student/certificates"   element={<ProtectedRoute roles={['student']} loginPath="/student/login"><StudentLayout><StudentCertificatesPage /></StudentLayout></ProtectedRoute>} />
        <Route path="/track/:studentId" element={<TrackingPage />} />

        {/* ── Role-specific Login Pages ── */}
        <Route path="/admin/login"      element={<AdminLoginPage />} />
        <Route path="/instructor/login" element={<InstructorLoginPage />} />
        <Route path="/student/login"    element={<StudentLoginPage />} />

        {/* ── Tribe Routes ── */}
        <Route path="/tribe"       element={<Navigate to="/tribe/login" replace />} />
        <Route path="/tribe/login" element={<TribeLoginPage />} />
        <Route path="/tribe/home"  element={
          <TribeProtectedRoute>
            <TribeDashboard />
          </TribeProtectedRoute>
        } />

        {/* ── Admin: Map Monitor (must be BEFORE the /admin/* wildcard) ── */}
        {/* ✅ FIXED: no pendingCount/isSuperAdmin props — the component
            reads isSuperAdmin from localStorage just like AdminLayout does,
            and pendingCount defaults to 0 inside the component */}
        <Route path="/admin/map-monitor" element={
          <ProtectedRoute roles={['superadmin', 'admin']} loginPath="/admin/login">
            <AdminMapMonitor />
          </ProtectedRoute>
        } />

        {/* ── Admin Protected Routes (wildcard — keep AFTER specific routes) ── */}
        <Route path="/admin/*" element={
          <ProtectedRoute roles={['superadmin', 'admin']} loginPath="/admin/login">
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* ── Admin Support (inside protected) ── */}
        <Route path="/admin/support" element={
          <ProtectedRoute roles={['superadmin', 'admin']} loginPath="/admin/login">
            <AdminLayout>
              <ContactSupport />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/testimonials" element={<AdminTestimonials />} />

        {/* ── Instructor Protected Routes ── */}
        <Route path="/instructor/*" element={
          <ProtectedRoute roles={['instructor']} loginPath="/instructor/login">
            <InstructorDashboard />
          </ProtectedRoute>
        } />

        {/* ── Student Protected Routes ── */}
        <Route path="/student/*" element={
          <ProtectedRoute roles={['student']} loginPath="/student/login">
            <StudentDashboard />
          </ProtectedRoute>
        } />

        {/* ── Student Support (inside protected) ── */}
        <Route path="/student/support" element={
          <ProtectedRoute roles={['student']} loginPath="/student/login">
            <StudentLayout>
              <StudentSupport />
            </StudentLayout>
          </ProtectedRoute>
        } />

        {/* ── Fallback ── */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;